import classNames from "classnames";
import { FunctionComponent } from "react";
import { SlideImage } from "yet-another-react-lightbox";
import { selectActiveSite } from "../selectors/sites.js";
import { useAppSelector } from "../store/hooks.js";
import styles from "../stylesheets/components/image-gallery-layout-6.module.css";
import {
  ColorScheme,
  ContainerQueries,
  ImageModuleSettings,
  Language,
  TitleAndSubtitleTextAlign,
  TranslatedModule,
} from "../types/index.js";
import {
  getGalleryMediaAspectRatio,
  getPlaceholderImage,
} from "../utils/utils.js";
import ModuleHeadings from "./ModuleHeadings.js";
import ImageModule from "./Modules/ImageModule.js";

interface Props {
  title: string;
  subtitle: string;
  textAlign: TitleAndSubtitleTextAlign;
  scheme: ColorScheme;
  isFirstOnPage: boolean;
  imageModules: TranslatedModule<ImageModuleSettings>[];
  queries: ContainerQueries;
  slides: SlideImage[];
  isPreview: boolean;
  languageId: Language;
  pageId: string;
  moduleId: string;
  onClickImage: (index: number) => void;
}

const ImageGalleryLayout6: FunctionComponent<Props> = ({
  title,
  subtitle,
  textAlign,
  isFirstOnPage,
  scheme,
  imageModules,
  queries,
  slides,
  isPreview,
  languageId,
  pageId,
  moduleId,
  onClickImage,
}) => {
  const site = useAppSelector(selectActiveSite);

  const maxImages = queries["Query--large"] ? 14 : 8;

  const images = imageModules.length
    ? imageModules.slice(0, maxImages)
    : new Array(8)
        .fill(true)
        .map((_, index) =>
          getPlaceholderImage({
            siteId: site.id,
            pageId,
            languageId,
            parentId: moduleId,
            index,
          }),
        );

  return (
    <div
      className={classNames(styles.ImageGalleryLayout6__Wrapper, {
        [styles["ImageGalleryLayout6__Wrapper--items-8"]]: images.length <= 8,
        [styles["ImageGalleryLayout6__Wrapper--items-14"]]: images.length > 8,
      })}
    >
      {images.map((imageModule, index) => {
        const item = slides[index];
        return (
          <a
            href={isPreview || !item ? undefined : item.src}
            className={styles.ImageGalleryLayout6__Item}
            key={imageModule.id}
            onClick={(e) => {
              e.preventDefault();
              onClickImage(index);
            }}
            style={{ gridArea: `img${index + 1}` }}
          >
            <ImageModule
              aspectRatio={getAspectRatio(index)}
              sizes="50vw"
              lazyLoad={true}
              width={1920}
              translatedModule={imageModule}
              isPreview={isPreview}
              isSlider={false}
              pageId={pageId}
              scheme={scheme}
              showOverlay={false}
            />
          </a>
        );
      })}
      {!title && <div className={styles.ImageGalleryLayout6__TextItem}></div>}
      <ModuleHeadings
        textAlign={textAlign}
        scheme={scheme}
        isFirstOnPage={isFirstOnPage}
        title={title}
        subtitle={subtitle}
        className={styles.ImageGalleryLayout6__Text}
        wrapperClassName={styles.ImageGalleryLayout6__TextItem}
      />
    </div>
  );
};

const getAspectRatio = (index: number): number => {
  return getGalleryMediaAspectRatio(index, "layout-6");
};

export default ImageGalleryLayout6;
